import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { BookOpen, Plus, Eye, PencilSimple, ArchiveBox, ArrowsClockwise } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STATUS_COLORS = { draft: '#F59E0B', submitted: '#3B82F6', approved: '#00F0FF', published: '#10B981', archived: '#555' };
const STATUS_FLOW = { draft: ['submitted'], submitted: ['approved', 'draft'], approved: ['published', 'draft'], published: ['archived'], archived: ['draft'] };

export default function PublisherRules() {
  const { publisherId } = useParams();
  const [searchParams] = useSearchParams();
  const gameFilter = searchParams.get('game');
  const [rulesets, setRulesets] = useState([]);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', version: '1.0', content: '', summary: '', quick_reference: '', game_id: '', is_current: false });

  const fetch = () => {
    const url = gameFilter
      ? `${API_URL}/api/publisher-portal/${publisherId}/rulesets?game_id=${gameFilter}`
      : `${API_URL}/api/publisher-portal/${publisherId}/rulesets`;
    axios.get(url, { withCredentials: true }).then(r => setRulesets(r.data || [])).catch(() => {}).finally(() => setLoading(false));
    axios.get(`${API_URL}/api/publisher-portal/${publisherId}/games`, { withCredentials: true }).then(r => setGames(r.data || [])).catch(() => {});
  };
  useEffect(fetch, [publisherId, gameFilter]);

  const create = async () => {
    if (!form.title) { toast.error('Title required'); return; }
    try {
      await axios.post(`${API_URL}/api/publisher-portal/${publisherId}/rulesets`, form, { withCredentials: true });
      toast.success('Ruleset created as draft'); setShowCreate(false);
      setForm({ title: '', version: '1.0', content: '', summary: '', quick_reference: '', game_id: '', is_current: false });
      fetch();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const save = async () => {
    if (!editing) return;
    try {
      await axios.put(`${API_URL}/api/publisher-portal/${publisherId}/rulesets/${editing.id}`, form, { withCredentials: true });
      toast.success('Ruleset updated'); setEditing(null);
      fetch();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const changeStatus = async (id, status) => {
    try {
      await axios.put(`${API_URL}/api/publisher-portal/${publisherId}/rulesets/${id}/status`, { status }, { withCredentials: true });
      toast.success(`Status: ${status}`); fetch();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const startEdit = (rs) => { setEditing(rs); setForm({ title: rs.title, version: rs.version, content: rs.content || '', summary: rs.summary || '', quick_reference: rs.quick_reference || '', game_id: rs.game_id || '', is_current: rs.is_current || false }); setShowCreate(false); };
  const cancelEdit = () => { setEditing(null); setForm({ title: '', version: '1.0', content: '', summary: '', quick_reference: '', game_id: '', is_current: false }); };

  const inputCls = "w-full bg-white/[0.03] border border-white/[0.08] rounded-lg px-3 py-2 text-white text-sm focus:border-[#00F0FF]/50 focus:outline-none placeholder-[#444]";

  const FormView = ({ onSubmit, label }) => (
    <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5 mb-6 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <input className={inputCls} placeholder="Ruleset title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="rule-title-input" />
        <div className="flex gap-3">
          <input className={inputCls} placeholder="Version (e.g. 1.0)" value={form.version} onChange={e => setForm({ ...form, version: e.target.value })} />
          <select className={inputCls} value={form.game_id} onChange={e => setForm({ ...form, game_id: e.target.value })}>
            <option value="">Select game...</option>
            {games.map(g => <option key={g.id || g.slug} value={g.id}>{g.name}</option>)}
          </select>
        </div>
      </div>
      <textarea className={inputCls + " h-48 resize-y font-mono text-xs"} placeholder="Full rules content (Markdown supported)..."
        value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} data-testid="rule-content-input" />
      <textarea className={inputCls + " h-16 resize-none"} placeholder="Short summary..."
        value={form.summary} onChange={e => setForm({ ...form, summary: e.target.value })} />
      <textarea className={inputCls + " h-16 resize-none"} placeholder="Quick reference sheet..."
        value={form.quick_reference} onChange={e => setForm({ ...form, quick_reference: e.target.value })} />
      <label className="flex items-center gap-2 text-sm text-[#888]">
        <input type="checkbox" checked={form.is_current} onChange={e => setForm({ ...form, is_current: e.target.checked })} className="accent-[#00F0FF]" />
        Mark as current/active ruleset
      </label>
      <div className="flex gap-2 justify-end">
        <button onClick={cancelEdit} className="px-4 py-2 text-sm text-[#888] hover:text-white transition-colors">Cancel</button>
        <button onClick={onSubmit} className="px-5 py-2 rounded-lg bg-[#00F0FF] text-black font-semibold text-sm hover:bg-[#00D4E0] transition-colors" data-testid="rule-submit-btn">{label}</button>
      </div>
    </div>
  );

  return (
    <div data-testid="publisher-rules-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight" style={{ fontFamily: 'Unbounded' }}>Rules</h1>
          <p className="text-sm text-[#666] mt-1">{rulesets.length} rulesets</p>
        </div>
        {!editing && (
          <button onClick={() => { setShowCreate(!showCreate); setEditing(null); }}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#00F0FF] text-black font-semibold text-sm"
            data-testid="create-ruleset-btn">
            <Plus size={16} weight="bold" /> New Ruleset
          </button>
        )}
      </div>

      {showCreate && !editing && <FormView onSubmit={create} label="Create Draft" />}
      {editing && <FormView onSubmit={save} label="Save Changes" />}

      <div className="space-y-3">
        {loading ? [...Array(2)].map((_, i) => <div key={i} className="h-24 bg-white/5 rounded-xl animate-pulse" />) :
        rulesets.length === 0 ? (
          <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-10 text-center">
            <BookOpen size={44} className="mx-auto text-[#333] mb-4" />
            <p className="text-white font-medium text-lg mb-1">No Rulesets Yet</p>
            <p className="text-sm text-[#555]">Create your first ruleset for a game.</p>
          </div>
        ) : rulesets.map(rs => {
          const gameName = games.find(g => g.id === rs.game_id)?.name || '';
          const nextStatuses = STATUS_FLOW[rs.status] || [];
          return (
            <div key={rs.id} className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <BookOpen size={20} weight="duotone" className="text-[#00F0FF]" />
                  <div>
                    <p className="text-white font-semibold">{rs.title}</p>
                    <div className="flex items-center gap-2 text-[11px] text-[#555]">
                      <span>v{rs.version}</span>
                      {gameName && <span>· {gameName}</span>}
                      {rs.is_current && <span className="text-[#00F0FF]">· Current</span>}
                    </div>
                  </div>
                </div>
                <span className="text-[11px] px-2.5 py-1 rounded-full font-medium"
                  style={{ backgroundColor: `${STATUS_COLORS[rs.status] || '#555'}15`, color: STATUS_COLORS[rs.status] || '#555' }}>
                  {rs.status}
                </span>
              </div>
              {rs.summary && <p className="text-sm text-[#888] mt-2 line-clamp-2">{rs.summary}</p>}
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/[0.04]">
                <button onClick={() => startEdit(rs)} className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-white/5 text-[#888] hover:text-white transition-colors">
                  <PencilSimple size={12} /> Edit
                </button>
                {nextStatuses.map(s => (
                  <button key={s} onClick={() => changeStatus(rs.id, s)}
                    className="text-xs px-3 py-1.5 rounded-lg font-medium transition-colors"
                    style={{ backgroundColor: `${STATUS_COLORS[s]}12`, color: STATUS_COLORS[s] }}
                    data-testid={`status-${rs.id}-${s}`}>
                    {s === 'published' ? 'Publish' : s === 'submitted' ? 'Submit' : s === 'approved' ? 'Approve' : s === 'archived' ? 'Archive' : 'Revert'}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
